/**
 * Adjust the height of a textarea to fit its content without exceeding a maximum height.
 *
 * This function is useful for auto-growing textareas while typing.
 * It resets the textarea height to "auto" first, calculates the new height based on `scrollHeight`,
 * caps it at `maxHeight`, applies it to the textarea, and returns the new height.
 *
 * @param event - The input/textarea event containing the target HTMLTextAreaElement
 * @param maxHeight - Maximum height in pixels (default: 120)
 * @returns The new height in pixels applied to the textarea
 *
 * @example
 * <textarea (onInput)="updateTargetHeight($event)"></textarea>
 *
 * // Programmatically:
 * const newHeight = updateTargetHeight({ target: myTextarea } as Event, 150);
 */
export function updateTargetHeight(event: Event, maxHeight = 120): number {
  const target = event.target as HTMLTextAreaElement;
  target.style.height = 'auto'; // reset
  const newHeight = Math.min(target.scrollHeight, maxHeight);
  target.style.height = `${newHeight}px`;
  return newHeight;
}

/**
 * Watch an element's box and call back when it changes, where the browser can do it.
 *
 * `ResizeObserver` is not everywhere — a test environment is the usual case — and a component that
 * measures itself must go on drawing without it rather than fail to be created. Nothing is watched
 * then, and the measure a component takes when it draws stays its own business.
 *
 * @returns the function that stops watching, safe to call whether anything was watched or not.
 */
export function watchSize(element: Element, onResize: () => void): () => void {
  if (typeof ResizeObserver === 'undefined') return () => undefined;

  const observer = new ResizeObserver(onResize);
  observer.observe(element);
  return () => observer.disconnect();
}
